import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { candidatesApi, timelineApi } from '../../lib/api';
import {
  StickyNote, Clock, FileText, Send, Loader2, Trash2,
  Eye, Building2, Briefcase, ArrowRightCircle, Globe, Download,
  User, MapPin, Phone, Mail, Shield, Sparkles, TrendingUp, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CandidatePeekPanel({ candidate, open, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('summary');
  const [notes, setNotes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState({ notes: false, timeline: false, docs: false });
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    if (!candidate?.id) return;
    setNotes(candidate.notes_history || []);
    setDocuments(candidate.documents || []);
    setLoading(prev => ({ ...prev, timeline: true }));
    try {
      const res = await timelineApi.get(candidate.id);
      setTimeline(res.data.timeline || []);
    } catch {
      setTimeline([]);
    }
    setLoading(prev => ({ ...prev, timeline: false }));
  }, [candidate?.id]);

  useEffect(() => {
    if (open && candidate) loadData();
  }, [open, candidate, loadData]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSubmitting(true);
    try {
      const res = await candidatesApi.addNote(candidate.id, newNote.trim());
      setNotes(res.data.notes_history || []);
      setNewNote('');
      toast.success('Note added');
      onUpdate?.();
    } catch {
      toast.error('Failed to add note');
    }
    setSubmitting(false);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const res = await candidatesApi.deleteNote(candidate.id, noteId);
      setNotes(res.data.notes_history || []);
      toast.success('Note deleted');
      onUpdate?.();
    } catch {
      toast.error('Failed to delete note');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff}d ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    const num = typeof amount === 'string' ? parseInt(amount.replace(/[^0-9]/g, '')) : amount;
    if (num >= 10000000) return `${(num / 10000000).toFixed(1)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(1)} LPA`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toLocaleString('en-IN');
  };

  const timelineIcon = (type) => {
    switch (type) {
      case 'status_change': return <ArrowRightCircle className="w-3.5 h-3.5 text-blue-500" />;
      case 'profile_view': return <Eye className="w-3.5 h-3.5 text-slate-400" />;
      case 'note_added': return <StickyNote className="w-3.5 h-3.5 text-amber-500" />;
      default: return <Clock className="w-3.5 h-3.5 text-slate-400" />;
    }
  };

  if (!candidate) return null;

  const isPortal = candidate.source === 'public_submission' || candidate.source === 'portal';
  const salaryProgression = candidate.salary_progression || [];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[440px] sm:w-[520px] p-0 flex flex-col" data-testid="peek-panel">
        <SheetHeader className="px-5 pt-5 pb-3 border-b bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-base flex-shrink-0">
              {candidate.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base flex items-center gap-2">
                <span className="truncate">{candidate.name || 'Unknown'}</span>
                {isPortal && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-100 text-teal-700 border border-teal-200 flex-shrink-0" title="Uploaded via Candidate Portal">
                    <Globe className="w-3 h-3" />Portal
                  </span>
                )}
                {candidate.phone_verified && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 border border-green-200 flex-shrink-0">
                    <Shield className="w-3 h-3" />Verified
                  </span>
                )}
              </SheetTitle>
              <SheetDescription className="text-sm text-slate-500 truncate">
                {candidate.current_designation || candidate.current_company || candidate.email || 'No details'}
              </SheetDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {candidate.phone && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-white px-2 py-0.5 rounded border">
                <Phone className="w-3 h-3" />{candidate.phone}
              </span>
            )}
            {candidate.current_location && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-white px-2 py-0.5 rounded border">
                <MapPin className="w-3 h-3" />{candidate.current_location}
              </span>
            )}
            {candidate.total_experience_years && (
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-white px-2 py-0.5 rounded border">
                <Briefcase className="w-3 h-3" />{candidate.total_experience_years}y exp
              </span>
            )}
            {candidate.estimated_ctc && (
              <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">
                <IndianRupee className="w-3 h-3" />Est. {formatCurrency(candidate.estimated_ctc)}
              </span>
            )}
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-4 mt-3 mb-0 grid grid-cols-4 bg-slate-100">
            <TabsTrigger value="summary" className="text-xs gap-1" data-testid="peek-tab-summary">
              <Sparkles className="w-3.5 h-3.5" />Summary
            </TabsTrigger>
            <TabsTrigger value="notes" className="text-xs gap-1" data-testid="peek-tab-notes">
              <StickyNote className="w-3.5 h-3.5" />
              Notes {notes.length > 0 && <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 ml-0.5">{notes.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="timeline" className="text-xs gap-1" data-testid="peek-tab-timeline">
              <Clock className="w-3.5 h-3.5" />Timeline
            </TabsTrigger>
            <TabsTrigger value="documents" className="text-xs gap-1" data-testid="peek-tab-documents">
              <FileText className="w-3.5 h-3.5" />Docs
            </TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary" className="flex-1 overflow-hidden m-0 px-4 pt-3">
            <ScrollArea className="h-full">
              {candidate.ai_summary ? (
                <div className="space-y-4 pb-4">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />AI Executive Summary
                    </h4>
                    <div 
                      className="text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none [&_b]:text-slate-900 [&_i]:text-indigo-700 [&_u]:decoration-blue-400"
                      dangerouslySetInnerHTML={{ __html: candidate.ai_summary }}
                      data-testid="peek-ai-summary"
                    />
                  </div>

                  {/* Salary Progression */}
                  {salaryProgression.length > 0 && (
                    <div className="bg-white border rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5 text-green-600" />Career Salary Progression
                      </h4>
                      <div className="space-y-2">
                        {salaryProgression.map((entry, idx) => {
                          const maxCtc = Math.max(...salaryProgression.map(e => e.estimated_annual_ctc || 0));
                          const pct = maxCtc > 0 ? ((entry.estimated_annual_ctc || 0) / maxCtc) * 100 : 0;
                          return (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="text-xs text-slate-500 w-10 text-right font-mono">{entry.year}</span>
                              <div className="flex-1">
                                <div className="h-6 bg-slate-100 rounded-full overflow-hidden relative">
                                  <div 
                                    className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all"
                                    style={{ width: `${Math.max(pct, 8)}%` }}
                                  />
                                  <span className="absolute inset-0 flex items-center px-2 text-[10px] font-medium text-slate-700">
                                    {entry.role} — {formatCurrency(entry.estimated_annual_ctc)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {candidate.estimated_ctc && (
                        <div className="mt-3 pt-3 border-t flex items-center justify-between">
                          <span className="text-xs text-slate-500">Estimated Current CTC</span>
                          <span className="text-sm font-bold text-green-700">{formatCurrency(candidate.estimated_ctc)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No AI summary generated yet</p>
                  <p className="text-xs mt-1">Summary is generated when CV is parsed</p>
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="flex-1 flex flex-col overflow-hidden m-0 px-4 pt-3">
            <div className="flex gap-2 mb-3">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a quick note..."
                className="min-h-[60px] max-h-[80px] text-sm resize-none"
                data-testid="peek-note-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey) handleAddNote(); }}
              />
              <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim() || submitting} className="self-end h-9 px-3" data-testid="peek-add-note-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <ScrollArea className="flex-1">
              {notes.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <StickyNote className="w-8 h-8 mx-auto mb-2 opacity-40" />No notes yet
                </div>
              ) : (
                <div className="space-y-2 pb-4">
                  {[...notes].reverse().map((note) => (
                    <div key={note.id} className="group bg-amber-50/60 border border-amber-100 rounded-lg p-3" data-testid="peek-note-item">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{note.note}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[11px] text-slate-400">{note.added_by_name} &middot; {formatDate(note.added_at)}</span>
                        <button onClick={() => handleDeleteNote(note.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600" title="Delete note">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="flex-1 overflow-hidden m-0 px-4 pt-3">
            <ScrollArea className="h-full">
              {loading.timeline ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
              ) : timeline.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />No timeline entries yet
                </div>
              ) : (
                <div className="relative pb-4">
                  <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />
                  <div className="space-y-3">
                    {timeline.slice(0, 30).map((entry, i) => (
                      <div key={i} className="flex gap-3 relative" data-testid="peek-timeline-item">
                        <div className="mt-1 z-10 bg-white rounded-full p-0.5">{timelineIcon(entry.type)}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700">
                            {entry.type === 'status_change' && (<>Changed to <Badge variant="outline" className="text-[10px] px-1.5 py-0">{entry.new_status || entry.status}</Badge></>)}
                            {entry.type === 'profile_view' && (<>Viewed by <span className="font-medium">{entry.viewed_by || entry.user_name || 'Someone'}</span></>)}
                            {entry.type === 'note_added' && 'Note added'}
                            {!['status_change', 'profile_view', 'note_added'].includes(entry.type) && (<>{entry.description || entry.notes || entry.type}</>)}
                          </p>
                          {entry.company_name && (
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Building2 className="w-3 h-3" />{entry.company_name}{entry.job_title && <> &middot; {entry.job_title}</>}
                            </p>
                          )}
                          <p className="text-[11px] text-slate-400 mt-0.5">{formatDate(entry.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="flex-1 overflow-hidden m-0 px-4 pt-3">
            <ScrollArea className="h-full">
              {documents.length === 0 && !candidate.cv_file_path ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />No documents uploaded
                </div>
              ) : (
                <div className="space-y-2 pb-4">
                  {documents.map((doc, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-white border rounded-lg hover:border-blue-200 transition-colors" data-testid="peek-doc-item">
                      <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{doc.name || doc.filename || `Document ${i + 1}`}</p>
                        <p className="text-xs text-slate-400">{doc.type || 'File'} {doc.uploaded_at && `· ${formatDate(doc.uploaded_at)}`}</p>
                      </div>
                      {doc.url && (
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700"><Download className="w-4 h-4" /></a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {candidate.original_file_path && (
                <div className="mt-2 flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-700">Original CV</p>
                    <p className="text-xs text-blue-500 truncate">{candidate.original_file_name || 'Resume'}</p>
                  </div>
                  <a href={`${API_URL}/api/candidates/${candidate.id}/original-cv`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="border-t px-4 py-3 bg-slate-50">
          <Button size="sm" className="w-full" asChild>
            <Link to={`/ats/candidates/${candidate.id}`}>
              <Eye className="w-4 h-4 mr-2" />View Full Profile
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
